import { BffDownloadError } from "./downloadBffFile";

export type ExportCell =
  | { text: string; contractId?: string; eventId?: string }
  | { number: number }
  | { percent: number }
  | { date: string }
  | { dateTime: string }
  | null;

export interface ListSelection {
  selection: string;
  filteredOn?: string;
}

export interface ExportColumn {
  key: string;
  label: string;
}

export const EXPORT_PAGE_SIZE = 1000;
export const MAX_EXPORT_ROWS = 20_000;
export const MAX_EXPORT_CELLS = 300_000;

export const MAX_PDF_ROWS = 2_000;
export const MAX_PDF_CELLS = 30_000;

export class ExportTooLargeError extends Error {
  constructor() {
    super("The list has too many rows for one file");
  }
}

const TOO_LARGE_TO_DOWNLOAD = new Set([
  "zz_ContractApiErrorCode_ContractListTooLargeToDownload",
  "zz_ContractApiErrorCode_TaskListTooLargeToDownload",
  "zz_OrganizationApiErrorCode_AuditLogTooLargeToDownload",
  "zz_SourcingEventApiErrorCode_ProcurementListTooLargeToDownload",
]);

export const isTooLargeToDownload = (error: unknown): boolean =>
  error instanceof ExportTooLargeError ||
  (error instanceof BffDownloadError && error.errorCode !== null && TOO_LARGE_TO_DOWNLOAD.has(error.errorCode));

export type ExportLimit = "rows" | "cells";

const limitHit = (rows: number, columns: number, maxRows: number, maxCells: number): ExportLimit | null => {
  if (rows > maxRows) return "rows";
  return rows * columns > maxCells ? "cells" : null;
};

export const exportLimitHit = (rows: number, columns: number): ExportLimit | null =>
  limitHit(rows, columns, MAX_EXPORT_ROWS, MAX_EXPORT_CELLS);

export const pdfLimitHit = (rows: number, columns: number): ExportLimit | null =>
  limitHit(rows, columns, MAX_PDF_ROWS, MAX_PDF_CELLS);

export const exportTooLarge = (rows: number, columns: number): boolean => exportLimitHit(rows, columns) !== null;

export const pdfTooLarge = (rows: number, columns: number): boolean => pdfLimitHit(rows, columns) !== null;

export const fetchAllPages = async <T>(fetchPage: (skip: number, limit: number) => Promise<T[]>): Promise<T[]> => {
  const all: T[] = [];
  for (let skip = 0; ; skip += EXPORT_PAGE_SIZE) {
    const page = await fetchPage(skip, EXPORT_PAGE_SIZE);
    all.push(...page);
    if (page.length < EXPORT_PAGE_SIZE || all.length > MAX_EXPORT_ROWS) return all;
  }
};

export const exportRequest = <T>(
  columns: ExportColumn[],
  items: T[],
  cellFor: (key: string, item: T) => ExportCell,
  heading?: ListSelection,
) => ({
  columns: columns.map((column) => column.label),
  rows: items.map((item) => ({ cells: columns.map((column) => cellFor(column.key, item)) })),
  ...(heading?.selection ? { selection: heading.selection } : {}),
  ...(heading?.filteredOn ? { filteredOn: heading.filteredOn } : {}),
});

export const osloDate = (value: string): string =>
  new Date(value).toLocaleDateString("sv-SE", { timeZone: "Europe/Oslo" });

export const osloDateTime = (value: string): string => {
  const parts = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Oslo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`;
};
